import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputBase,
  styled,
  SvgIcon,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  colorLinkGrey,
  colorPrimary,
  colorSecondary,
  colorTextBlack,
  colorTextGrey
} from '../../config/theme';
import {
  AUDIO_ICON,
  BACK_ICON,
  LIKE_HEART_ICON,
  LIKE_ICON,
  OpenLinkIcon
} from '../../utils/imageUrls';
import { useLocation, useNavigate } from 'react-router-dom';
import { IOSSwitch } from '../../utils/styledLayout';
import ContentCards from './ContentCards';
import ReactPlayer from 'react-player';
import { VisibilityOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { getContentById } from '../../redux/reducers/contentSlice';
import {
  addContentViews,
  deleteGamification,
  deleteLikeContent,
  downloadContent,
  gamification,
  getAllContentBySection,
  getComments,
  getFavouriteListAll,
  getLibraryListAll,
  revertResources,
  saveLikeContent,
  updateAutoPlayStatus,
  updateLikeDislike
} from '../../redux/reducers/resourcesSlice';
import { useSelector } from 'react-redux';
import {
  getBasicDetails,
  getIsAutoPlayStatus,
  setIsAutoPlayStatus
} from '../../config/cookie';
import { formatDateTimeAgo, handleError } from '../../utils/utils';
import SnackBarBox from '../../components/SnackBarBox';
import MoreActionsComponent from './MoreActionsComponent';
import ShareButtonDialog from '../contentManagement/components/ShareButtonDialog';
import { saveAs } from 'file-saver';
import { getUserList } from '../../redux/reducers/usersSlice';
import CommentSection from './CommentSection';
import _ from 'lodash';
import { getGamificationPoints } from '../../redux/reducers/layoutSlice';
import ViewQuizModal from '../contentManagement/components/ViewQuizModal';

const getSecondPdfUrl = content =>
  content?.contentFileUrl2 ||
  content?.contentFile2Url ||
  content?.additionalContentFileUrl ||
  content?.secondContentFileUrl ||
  null;

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto'
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  // padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  right: 6,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

let searchValue = '';
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  height: '2rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 1.5),
    paddingRight: `calc(1em + ${theme.spacing(2)})`,
    transition: theme.transitions.create('width'),
    width: searchValue ? '15ch' : '0px',
    border: searchValue && '1px solid #CBD2D9',
    borderRadius: searchValue && '8px',
    [theme.breakpoints.up('sm')]: {
      '&:focus': {
        width: searchValue ? '15ch' : '15ch',
        border: '1px solid #CBD2D9',
        borderRadius: '8px'
      }
    }
  }
}));

export const CustomProgressBar = ({ duration, playedSeconds, markers }) => {
  const percentagePlayed = (playedSeconds / duration) * 100;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '8px',
        background: '#ccc',
        borderRadius: '4px'
      }}
    >
      {/* Progress fill */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          height: '100%',
          width: `${percentagePlayed}%`,
          backgroundColor: '#4caf50',
          borderRadius: '4px'
        }}
      />

      {/* Markers */}
      {markers.map((markerTime, index) => {
        const markerPosition = (markerTime / duration) * 100;
        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${markerPosition}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: 'red',
              zIndex: 2
            }}
          />
        );
      })}
    </div>
  );
};

const ViewContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { authData } = useSelector(state => state.auth);
  const { contentDetails, isGetContentSuccess, isGetContentLoading } =
    useSelector(state => state.content);
  const {
    sectionWiseContentList,
    isSectionWiseContentSuccess,
    isUpdateLikeDislikeLoading,
    isUpdateLikeDislikeSuccess,
    isUpdateLikeDislikeFailed,
    updateLikeDislikeResponse,
    updateLikeDislikeErrorContainer,
    isSaveToWatchLaterLoading,
    isSaveToWatchLaterSuccess,
    isSaveToWatchLaterFailed,
    saveToWatchLaterResponse,
    saveToWatchLaterErrorContainer,
    isShareDetailsLoading,
    isShareDetailsSuccess,
    isShareDetailsFailed,
    shareDetailsResponse,
    shareDetailsErrorContainer,
    isSectionWiseContentLoading,
    isCommentDataLoading,
    isCommentDataSuccess,
    commentsData,
    isAddCommentsLoading,
    isAddCommentsSuccess,
    isAddCommentsFailed,
    addCommentsResponse,
    addCommentsErrorContainer,
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationSuccess,
    isDeleteGamificationLoading,
    libraryListAll,
    isGetLibraryListAllSuccess,
    favouriteListAll,
    isGetFavouriteListAllSuccess,
    isSaveLikeContentLoading,
    isSaveLikeContentSuccess,
    isDeleteLikeContentLoading,
    isDeleteLikeContentSuccess
  } = useSelector(state => state.resource);

  const basicDetails = JSON.parse(getBasicDetails());
  const urlParams = new URLSearchParams(location.search);
  const encryptId = urlParams.get('id');
  const sectionId = urlParams.get('sectionId');
  const tags = location?.state?.tags || [];
  const path = location?.state?.path || '';

  const defaultExcelSvg = '/images/default-excel.svg';
  const defaultWordSvg = '/images/default-word.svg';
  const defaultPowerPointSvg = '/images/default-powerpoint.svg';
  const defaultPdfSvg = '/images/default-pdf.svg';

  const [isPlaying, setIsPlaying] = useState(true);
  const [viewContent, setViewContent] = useState();
  const [contentList, setContentList] = useState([]);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [commentsList, setCommentList] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);

  const [encryptIds, setEncryptIds] = useState([encryptId]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [quizTime, setQuizTime] = useState('');
  const [isPausedByShare, setIsPausedByShare] = useState(false);

  const playerRef = useRef(null);

  const [quizFlashcards, setQuizFlashcards] = useState([]);
  const [triggeredFlashcards, setTriggeredFlashcards] = useState([]);
  const [currentQuiz, setCurrentQuiz] = useState(0);

  const autoPlay = getIsAutoPlayStatus();
  const uploadType = viewContent?.uploadType;
  const secondPdfUrl = getSecondPdfUrl(viewContent);
  const userId = authData?.userId || 0;
  const userName = authData?.userName || '';

  const handleLikeDislike = (e, video) => {
    e.stopPropagation();

    let LikePayload = {
      contentId: video?.contentId || 0,
      loggedInUserName: userName || ''
    };

    let disLikePayload = {
      contentId: video?.contentId || 0,
      loggedInUserName: userName || '',
      userId: userId || 0
    };

    let payload = video?.contentLikes === 'No' ? LikePayload : disLikePayload;
    let contentLike = video?.contentLikes;

    dispatch(updateLikeDislike({ payload, contentLike }));

    // if (authData?.role === 'I am a student') {
    //   if (video?.allow_points && video?.contentLikes === 'Yes') {
    //     let payload = {
    //       userId: userId || 0,
    //       contentId: video?.contentId || 0,
    //       loggedInUserName: userName || ''
    //     };
    //     dispatch(deleteGamification({ payload }));
    //   }

    //   if (video?.allow_points && video?.contentLikes === 'No') {
    //     let gamificationPayload = {
    //       userId: userId || 0,
    //       contentId: video?.contentId || 0,
    //       point: video?.likes_points || 0,
    //       type: 'like',
    //       loggedInUserName: userName || ''
    //     };
    //     dispatch(gamification({ gamificationPayload }));
    //   }
    // }
  };

  useEffect(() => {
    console.log('inside useeFFECT');
    if (isAutoPlay) {
      setIsPlaying(true);
    }
  }, [isAutoPlay]);

  const circleStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: '#c9c9c9',
    color: 'white',
    fontSize: '18px',
    marginRight: '5px'
  };

  useEffect(() => {
    if (encryptId) {
      let viewFlag = true;
      dispatch(getContentById({ encryptId, userId, viewFlag }));
      dispatch(
        getUserList({
          payload: { page: 0, size: 0, search: '', type: 'all' }
        })
      );
      dispatch(revertResources());
    }
  }, [encryptId]);

  useEffect(() => {
    if (autoPlay) {
      setIsAutoPlay(autoPlay?.autoPlayStatus);
    }
  }, []);

  useEffect(() => {
    if (contentDetails && isGetContentSuccess) {
      setViewContent(contentDetails);
      handleGetComments(contentDetails?.contentId);

      const parsedFlashcards =
        contentDetails?.flashCard?.map(card => {
          const [min = 0, sec = 0] = (card.flashcardTime || '00:00')
            .split(':')
            .map(Number);
          return {
            ...card,
            timeInSeconds: min * 60 + sec
          };
        }) || [];

      setQuizFlashcards(parsedFlashcards);
      setTriggeredFlashcards([]); // Reset when new content loads
    }
  }, [contentDetails, isGetContentSuccess]);

  const handleGetComments = contentId => {
    let payload = {
      page: 0,
      size: 1000,
      type: 'all'
    };

    dispatch(getComments({ payload, contentId }));
  };

  useEffect(() => {
    if (sectionId) {
      if (basicDetails.gradeId) {
        getAllContentBySectionAPI();
      }
    }
  }, [sectionId, encryptId, searchTerm, isSaveToWatchLaterSuccess, isAutoPlay]);

  const getAllContentBySectionAPI = () => {
    if (path.includes('/content-space/library')) {
      const payload = {
        userId: authData?.userId,
        contentType: 'All',
        startDate: '',
        endDate: '',
        dateType: 'all',
        section: location?.state?.sectionId,
        encryptedContentId: isAutoPlay
          ? encryptIds.length === 1
            ? encryptId
            : encryptIds.join(',')
          : encryptId,
        paginationRequest: {
          type: 'all',
          page: 0,
          size: 20
        }
      };
      const search = searchTerm;
      dispatch(getLibraryListAll({ payload, search }));
    } else if (path.includes('/content-space/favourites')) {
      const payload = {
        userId: authData?.userId,
        contentType: 'All',
        startDate: location.state?.apiDate,
        endDate: location.state?.apiDate,
        dateType: 'selected_date',
        encryptedContentId: isAutoPlay
          ? encryptIds.length === 1
            ? encryptId
            : encryptIds.join(',')
          : encryptId,
        paginationRequest: {
          type: 'all',
          page: 0,
          size: 20
        }
      };
      const search = searchTerm;
      dispatch(getFavouriteListAll({ payload, search }));
    } else {
      let payload = {
        page: 0,
        size: 15,
        type: 'all'
      };
      let queryParam = {
        search: searchTerm || '',
        grade: basicDetails?.gradeId || '',
        sectionId: sectionId,
        userId: userId || 0,
        autoPlayFlag: isAutoPlay ? 'Yes' : '',
        encryptedContentId: isAutoPlay
          ? encryptIds.length === 1
            ? encryptId
            : encryptIds.join(',')
          : encryptId,
        tags: tags ? tags.join(',') : ''
      };
      dispatch(getAllContentBySection({ payload, queryParam }));
    }
  };

  useEffect(() => {
    if (isUpdateLikeDislikeSuccess) {
      getAllContentBySectionAPI();
      dispatch(getContentById({ encryptId, userId }));
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
  }, [isUpdateLikeDislikeLoading, isUpdateLikeDislikeSuccess]);

  useEffect(() => {
    if (sectionWiseContentList && isSectionWiseContentSuccess) {
      setContentList(sectionWiseContentList?.payload?.dataList || []);
    } else if (libraryListAll?.contentList && isGetLibraryListAllSuccess) {
      setContentList(libraryListAll?.contentList || []);
    } else if (favouriteListAll?.contentList && isGetFavouriteListAllSuccess) {
      setContentList(favouriteListAll?.contentList || []);
    }
  }, [
    sectionWiseContentList,
    isSectionWiseContentSuccess,
    libraryListAll,
    isGetLibraryListAllSuccess,
    isGetFavouriteListAllSuccess
  ]);

  useEffect(() => {
    if (commentsData && isCommentDataSuccess) {
      setCommentList(commentsData?.payload?.dataList || []);
      setCommentsCount(commentsData?.payload?.totalCount || 0);
    }
  }, [isCommentDataSuccess, isCommentDataLoading]);

  const handleAutoPlay = e => {
    let payload = {
      userId: userId || 0,
      autoPlayStatus: e?.target?.checked,
      loggedInUserName: userName || ''
    };
    dispatch(updateAutoPlayStatus({ payload }));
    setIsAutoPlay(e?.target?.checked);
    setIsAutoPlayStatus(e?.target?.checked);
  };

  const handleAutoPlayVideo = () => {
    if (isAutoPlay) {
      setIsPlaying(true);
      const result = contentList.filter(
        item =>
          item.upload_type === 'Video' &&
          item.encrypted_content_id !== encryptId
      );
      if (result.length > 0) {
        handleNavigate(result[0]);
      }
    }
  };

  const handleNavigate = result => {
    const url = `/content-space/view?id=${result?.encrypted_content_id}&sectionId=${sectionId}`;
    navigate(url);

    let tempEncryptedIds = [...encryptIds];
    tempEncryptedIds.push(result?.encrypted_content_id);
    setEncryptIds(tempEncryptedIds);
  };

  const subTitle = [
    viewContent?.videoCount > 0 && `${viewContent?.videoCount} videos`,
    viewContent?.documentCount > 0 && `${viewContent?.documentCount} documents`,
    viewContent?.flashCardsCount > 0 &&
      `${viewContent?.flashCardsCount} flash cards`,
    viewContent?.audioCount > 0 && `${viewContent?.audioCount} audio`
  ]
    .filter(Boolean)
    .join(' | ');

  const handleOpenInGoogleDocs = (data, fileUrl) => {
    let payload = {
      contentId: viewContent?.contentId || 0,
      userId: authData?.userId || 0,
      loggedInUserName: authData?.userName || ''
    };
    dispatch(addContentViews({ payload }));

    const urlToOpen = fileUrl || data?.contentFileUrl;

    if (data?.uploadType === 'PDF' || data?.uploadType === 'Image') {
      window.open(urlToOpen, '_blank');
    } else {
      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(urlToOpen)}`;
      window.open(googleDocsUrl, '_blank');
    }
  };

  const handleDownload = async data => {
    if (viewContent?.download) {
      let payload = {
        contentId: data?.contentId || 0,
        userId: userId || 0,
        loggedInUserName: userName || ''
      };
      dispatch(downloadContent({ payload }));

      fetch(data?.contentFileUrl)
        .then(response => response.blob())
        .then(blob => {
          saveAs(blob, data?.contentFileName);
        })
        .catch(error => {
          console.error('Error downloading the image:', error);
        });

      if (authData?.role === 'I am a student') {
        if (data?.allow_points) {
          let gamificationPayload = {
            userId: userId || 0,
            contentId: data?.contentId || 0,
            point: data?.download_points || 0,
            type: 'download',
            loggedInUserName: userName || ''
          };
          dispatch(gamification({ gamificationPayload }));
        }
      }
    }
  };

  const handleCloseShareDialog = e => {
    // e.stopPropagation();
    setIsShareDialogOpen(false);
    setIsPlaying(true);
  };

  const handleCloseQuizModal = e => {
    // e.stopPropagation();
    setIsQuizModalOpen(false);
    setCurrentQuiz(prevState => prevState + 1);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (isShareDialogOpen) {
      setIsPlaying(false);
    }
    if (isQuizModalOpen) {
      setIsPlaying(false);
    }
  }, [isShareDialogOpen, isQuizModalOpen]);

  const handleVideoClick = () => {
    if (isPausedByShare) {
      if (playerRef.current) {
        playerRef.current.seekTo(watchTime, 'seconds');
      }
      setIsPlaying(!isPlaying);
      setIsPausedByShare(false);
    } else {
      setIsPlaying(!isPlaying);
      setIsShareDialogOpen(false);
    }
  };

  const handleOnShareClick = e => {
    e.stopPropagation();
    if (viewContent?.share && playerRef.current) {
      setIsShareDialogOpen(true);
      const currentTime = playerRef.current.getCurrentTime();
      setWatchTime(currentTime); // Save current time
      // setIsPlaying(!isPlaying); // Pause the video
    }
  };

  const handleOnReportClick = () => {
    if (viewContent?.share && playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      setWatchTime(currentTime); // Save current time
    }
    setIsPlaying(!isPlaying);
  };
  const handleCommentList = id => {
    setCommentList(prevList => setIsShow(id, prevList));
  };

  const setIsShow = (id, list) => {
    return list.map(item => {
      const newItem = { ...item, isShow: item.id === id };
      if (newItem.replies && newItem.replies.length > 0) {
        newItem.replies = setIsShow(id, newItem.replies);
      }
      return newItem;
    });
  };

  const description = viewContent?.description || '';
  const truncatedDescription =
    description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleUpdateComment = () => {
    handleGetComments(viewContent?.contentId);
  };

  const debouncedSearch = _.debounce(term => setSearchTerm(term), 300);

  const handleSearch = e => {
    const term = e.target.value;
    if (term.length >= 3 || term.length === 0) {
      debouncedSearch(term);
    }
  };

  useEffect(() => {
    if (!isGamificationLoading && isGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
    if (!isDeleteGamificationLoading && isDeleteGamificationSuccess) {
      dispatch(getGamificationPoints(authData?.userId || 0));
    }
  }, [
    isGamificationLoading,
    isGamificationSuccess,
    isDeleteGamificationLoading,
    isDeleteGamificationSuccess
  ]);

  // Video watching time

  const [totalTime, setTotalTime] = useState(0);
  const [watchTime, setWatchTime] = useState(0);
  const [callAPI, setCallAPI] = useState(true);

  // useEffect(() => {
  //   if (isPlaying && playerRef.current) {
  //     console.log("video time")
  //     playerRef.current.seekTo(watchTime, 'seconds');
  //   }
  // }, [isPlaying]);

  const handleDuration = duration => {
    setTotalTime(duration);
  };

  const handleProgress = progress => {
    console.log('speed');
    setWatchTime(progress.playedSeconds);

    const currentTime = Math.floor(progress.playedSeconds);

    // Check flashcards due at this time
    const dueFlashcard = quizFlashcards.find(
      card =>
        card.timeInSeconds === currentTime &&
        !triggeredFlashcards.includes(card.flashcardId)
    );

    if (dueFlashcard) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }

      if (playerRef.current) {
        const exactTime = playerRef.current.getCurrentTime();
        setWatchTime(exactTime);
      }

      setIsPlaying(false);

      setIsQuizModalOpen(true);
      setTriggeredFlashcards(prev => [...prev, dueFlashcard.flashcardId]);
    }
  };

  const checkWatchProgress = () => {
    if (totalTime > 0 && watchTime >= totalTime * 0.7) {
      setCallAPI(false);
      let payload = {
        contentId: viewContent?.contentId || 0,
        userId: authData?.userId || 0,
        loggedInUserName: userName || ''
      };
      dispatch(addContentViews({ payload }));
    }
  };

  useEffect(() => {
    if (callAPI) {
      checkWatchProgress();
    }
  }, [watchTime]);

  const handleLikeContent = data => {
    if (viewContent?.likes) {
      if (data?.isContentLiked == 'No') {
        let payload = {
          contentId: data?.contentId || 0,
          loggedInUserName: userName || ''
        };
        dispatch(saveLikeContent({ payload }));
      } else {
        let payload = {
          contentId: data?.contentId || 0,
          userId: authData?.userId || 0,
          loggedInUserName: userName || ''
        };
        dispatch(deleteLikeContent({ payload }));
      }

      if (authData?.role === 'I am a student') {
        if (data?.allow_points && data?.isContentLiked === 'Yes') {
          let payload = {
            userId: userId || 0,
            contentId: data?.contentId || 0,
            loggedInUserName: userName || ''
          };
          dispatch(deleteGamification({ payload }));
        }

        if (data?.allow_points && data?.isContentLiked === 'No') {
          let gamificationPayload = {
            userId: userId || 0,
            contentId: data?.contentId || 0,
            point: data?.likes_points || 0,
            type: 'like',
            loggedInUserName: userName || ''
          };
          dispatch(gamification({ gamificationPayload }));
        }
      }
    }
  };

  useEffect(() => {
    if (
      (!isSaveLikeContentLoading && isSaveLikeContentSuccess) ||
      (!isDeleteLikeContentLoading && isDeleteLikeContentSuccess)
    ) {
      dispatch(getContentById({ encryptId, userId }));
    }
  }, [
    isSaveLikeContentLoading,
    isSaveLikeContentSuccess,
    isDeleteLikeContentLoading,
    isDeleteLikeContentSuccess
  ]);

  return (
    <>
      {isGetContentLoading ? (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress />
          </div>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px'
            }}
          >
            <SvgIcon
              sx={{ marginRight: 2, cursor: 'pointer' }}
              onClick={() => navigate(-1)}
            >
              {BACK_ICON}
            </SvgIcon>

            <Typography
              sx={{
                fontSize: '32px',
                fontWeight: '700',
                color: colorTextBlack
              }}
            >
              {viewContent?.section}
            </Typography>
            {/* <Typography
              sx={{
                fontSize: '20px',
                fontWeight: '700',
                color: colorTextBlack,
                marginLeft: '10px',
                marginTop: '5px'
              }}
            >
              {`(${subTitle})`}
            </Typography> */}
          </Box>
          <Box
            sx={{
              marginBottom: 4,
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '8px'
            }}
          >
            <Grid container>
              <Grid item sx={{ flex: 6 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginRight: '20px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: uploadType !== 'Video' && 'relative'
                    }}
                  >
                    {uploadType === 'Video' ? (
                      <div style={{ position: 'relative', width: '100%' }}>
                        <ReactPlayer
                          ref={playerRef}
                          url={viewContent?.contentFileUrl || ''}
                          playing={isPlaying}
                          controls={true}
                          onPlay={() => {
                            setIsPlaying(true);
                          }}
                          onPause={() => {
                            setIsPlaying(false);
                          }}
                          width="100%"
                          height="auto"
                          style={{ borderRadius: '12px' }}
                          onEnded={handleAutoPlayVideo}
                          config={{
                            file: {
                              attributes: {
                                controlsList: 'nodownload'
                              }
                            }
                          }}
                          onDuration={handleDuration}
                          onProgress={handleProgress}
                        />

                        {/* Like Button (still custom, optional) */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '3vh',
                            right: '1vw',
                            width: '26px',
                            height: '26px',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 100,
                            cursor: 'pointer'
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            handleLikeDislike(e, viewContent);
                          }}
                        >
                          <img
                            src={
                              viewContent?.contentLikes === 'Yes'
                                ? LIKE_HEART_ICON
                                : LIKE_ICON
                            }
                            alt="Like"
                            width={15}
                            height={15}
                          />
                        </div>
                      </div>
                    ) : uploadType === 'Audio' ? (
                      <>
                        <ReactPlayer
                          url={viewContent?.contentFileUrl || ''}
                          playing={true}
                          controls={true}
                          width="100%"
                          style={{ borderRadius: '12px' }}
                          config={{
                            file: {
                              attributes: {
                                controlsList: 'nodownload'
                              }
                            }
                          }}
                          onDuration={handleDuration}
                          onProgress={handleProgress}
                        />

                        <div
                          style={{
                            position: 'absolute',
                            zIndex: '10',
                            bottom: '60px',
                            width: '100%',
                            height: '80%'
                          }}
                        >
                          <img
                            src={AUDIO_ICON}
                            alt="Audio"
                            style={{ width: '100%', height: '100%' }}
                          />
                        </div>

                        <div
                          style={{
                            position: 'absolute',
                            top: '20px',
                            right: '8px',
                            width: '26px',
                            height: '26px',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            borderRadius: '50%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10,
                            cursor: 'pointer'
                          }}
                          onClick={e => handleLikeDislike(e, viewContent)}
                        >
                          <img
                            className="imgbox"
                            src={
                              viewContent?.contentLikes === 'Yes'
                                ? LIKE_HEART_ICON
                                : LIKE_ICON
                            }
                            alt="Like Icon"
                            width={15}
                            height={15}
                            style={{
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      </>
                    ) : uploadType === 'POWERPOINT' ||
                      uploadType === 'MSWORD' ||
                      uploadType === 'EXCEL' ||
                      uploadType === 'PDF' ||
                      uploadType === 'Image' ? (
                      <>
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            borderRadius: '12px'
                          }}
                        >
                          <img
                            src={
                              uploadType === 'POWERPOINT'
                                ? defaultPowerPointSvg
                                : uploadType === 'MSWORD'
                                  ? defaultWordSvg
                                  : uploadType === 'EXCEL'
                                    ? defaultExcelSvg
                                    : uploadType === 'PDF'
                                      ? defaultPdfSvg
                                      : uploadType === 'Image'
                                        ? viewContent?.contentFileUrl
                                        : defaultExcelSvg
                            }
                            alt="Audio"
                            style={{
                              width: '100%',
                              height: '350px',
                              borderRadius: '12px'
                            }}
                          />

                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              backgroundColor: `${uploadType !== 'Image' ? 'rgba(128, 128, 128, 0.5)' : ''}`,
                              borderRadius: '12px'
                            }}
                          />

                          <div
                            style={{
                              position: 'absolute',
                              bottom: 15,
                              left: 15,
                              display: 'flex',
                              gap: '8px'
                            }}
                          >
                            <div
                              style={{
                                backgroundColor: '#FFFFFF',
                                borderRadius: '8px',
                                padding: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() =>
                                handleOpenInGoogleDocs(viewContent)
                              }
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Typography
                                  style={{
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    fontWeight: 700,
                                    fontFamily: 'Lato',
                                    color: colorTextBlack,
                                    marginRight: '5px'
                                  }}
                                >
                                  {uploadType === 'PDF' && secondPdfUrl
                                    ? 'Open PDF 1'
                                    : 'Open Link'}
                                </Typography>

                                <OpenLinkIcon />
                              </div>
                            </div>
                            {uploadType === 'PDF' && secondPdfUrl && (
                              <div
                                style={{
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '8px',
                                  padding: '12px',
                                  cursor: 'pointer'
                                }}
                                onClick={() =>
                                  handleOpenInGoogleDocs(
                                    viewContent,
                                    secondPdfUrl
                                  )
                                }
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Typography
                                    style={{
                                      fontSize: '14px',
                                      lineHeight: '18px',
                                      fontWeight: 700,
                                      fontFamily: 'Lato',
                                      color: colorTextBlack,
                                      marginRight: '5px'
                                    }}
                                  >
                                    Open PDF 2
                                  </Typography>
                                  <OpenLinkIcon />
                                </div>
                              </div>
                            )}
                          </div>

                          <div
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              width: '26px',
                              height: '26px',
                              backgroundColor: 'rgba(255, 255, 255, 0.7)',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={e => handleLikeDislike(e, viewContent)}
                          >
                            <img
                              className="imgbox"
                              src={
                                viewContent?.contentLikes === 'Yes'
                                  ? LIKE_HEART_ICON
                                  : LIKE_ICON
                              }
                              alt="Like Icon"
                              width={15}
                              height={15}
                              style={{
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>

                  {/* Content start */}
                  <div style={{ display: 'flex', marginTop: '10px' }}>
                    <Typography
                      style={{
                        fontSize: '24px',
                        fontWeight: 700,
                        lineHeight: '32px',
                        fontFamily: 'Lato',
                        color: colorTextBlack
                      }}
                    >
                      {viewContent?.title || ''}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'end',
                      marginTop: '20px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div>
                        {viewContent?.profileImageUrl ? (
                          <Avatar
                            src={viewContent?.profileImageUrl || ''}
                            alt={viewContent?.profileImageUrl || ''}
                            sx={{ width: 44, height: 44, marginRight: 1 }}
                          />
                        ) : (
                          <div style={circleStyle}>
                            {viewContent
                              ? viewContent?.userName
                                  ?.split(' ')[0]
                                  .charAt(0)
                                  .toUpperCase()
                              : 'A'}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                          style={{
                            fontSize: '16px',
                            fontWeight: 700,
                            lineHeight: '20px',
                            fontFamily: 'Lato',
                            color: colorLinkGrey,
                            marginBottom: '3px'
                          }}
                        >
                          {viewContent?.userName || ''}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#ABB4BD',
                            gap: 1,
                            fontWeight: 500,
                            fontFamily: 'Lato',
                            fontSize: '14px',
                            lineHeight: '18px'
                          }}
                        >
                          <VisibilityOutlined fontSize="small" />
                          <Typography
                            style={{
                              fontSize: '14px',
                              fontFamily: 'Lato',
                              lineHeight: '18px',
                              fontWeight: 500,
                              color: colorTextGrey
                            }}
                          >
                            {viewContent?.views || 0}
                          </Typography>

                          <Typography
                            style={{
                              fontSize: '14px',
                              fontFamily: 'Lato',
                              lineHeight: '18px',
                              fontWeight: 500,
                              color: colorTextGrey
                            }}
                          >
                            •
                          </Typography>
                          <Typography
                            style={{
                              fontSize: '14px',
                              fontFamily: 'Lato',
                              lineHeight: '18px',
                              fontWeight: 500,
                              color: colorTextGrey
                            }}
                          >
                            {viewContent?.updatedDate
                              ? formatDateTimeAgo(viewContent?.updatedDate)
                              : '-'}
                          </Typography>
                        </Box>
                      </div>
                    </div>

                    <div>
                      <Box>
                        <Tooltip
                          placement="top"
                          title={
                            !viewContent?.likes &&
                            'Likes for the content is disabled.'
                          }
                        >
                          <Button
                            variant="outlined"
                            sx={{
                              px: 3,
                              borderColor: viewContent?.likes
                                ? colorPrimary
                                : colorSecondary,
                              cursor: viewContent?.likes
                                ? 'pointer'
                                : 'not-allowed',
                              fontSize: '12px',
                              fontWeight: 700,
                              marginRight: '10px',
                              borderRadius: '8px',
                              color: viewContent?.likes
                                ? 'black'
                                : colorTextGrey,
                              borderWidth: '2px',
                              height: '34px',
                              '&:hover': {
                                borderColor: viewContent?.likes
                                  ? colorPrimary
                                  : colorSecondary,
                                backgroundColor: 'transparent',
                                borderRadius: '10px',
                                borderWidth: '2px'
                              }
                            }}
                            startIcon={
                              viewContent?.isContentLiked === 'Yes' ? (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  {viewContent?.likes && (
                                    <path
                                      d="M11.8948 11.3493C11.7635 12.203 11.0289 12.8332 10.1651 12.8332H2.33317C1.68884 12.8332 1.1665 12.3108 1.1665 11.6665V7.58317C1.1665 6.93884 1.68884 6.4165 2.33317 6.4165H3.70408C3.9346 6.4165 4.14351 6.28074 4.23713 6.07008L6.28995 1.45124C6.36691 1.27809 6.53862 1.1665 6.7281 1.1665C7.52251 1.1665 8.1665 1.8105 8.1665 2.60491V4.6665C8.1665 4.98867 8.42767 5.24984 8.74984 5.24984H10.7934C11.8659 5.24984 12.6861 6.20586 12.523 7.26594L11.8948 11.3493Z"
                                      fill="#F4C24A"
                                    />
                                  )}

                                  <path
                                    d="M4.08317 12.8332V6.4165M1.1665 7.58317V11.6665C1.1665 12.3108 1.68884 12.8332 2.33317 12.8332H10.1651C11.0289 12.8332 11.7635 12.203 11.8948 11.3493L12.523 7.26594C12.6861 6.20586 11.8659 5.24984 10.7934 5.24984H8.74984C8.42767 5.24984 8.1665 4.98867 8.1665 4.6665V2.60491C8.1665 1.8105 7.52251 1.1665 6.7281 1.1665C6.53862 1.1665 6.36691 1.27809 6.28995 1.45124L4.23713 6.07008C4.14351 6.28074 3.9346 6.4165 3.70408 6.4165H2.33317C1.68884 6.4165 1.1665 6.93884 1.1665 7.58317Z"
                                    stroke={
                                      viewContent?.likes ? '#EA7804' : '#ABB4BD'
                                    }
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M4.08333 12.8327V6.41602M1.16667 7.58268V11.666C1.16667 12.3103 1.689 12.8327 2.33333 12.8327H10.1653C11.0291 12.8327 11.7636 12.2025 11.895 11.3488L12.5232 7.26545C12.6863 6.20537 11.8661 5.24935 10.7935 5.24935H8.75C8.42783 5.24935 8.16667 4.98818 8.16667 4.66602V2.60442C8.16667 1.81001 7.52267 1.16602 6.72826 1.16602C6.53878 1.16602 6.36707 1.2776 6.29012 1.45075L4.2373 6.0696C4.14367 6.28025 3.93477 6.41602 3.70424 6.41602H2.33333C1.689 6.41602 1.16667 6.93835 1.16667 7.58268Z"
                                    stroke={
                                      viewContent?.likes ? '#EA7804' : '#ABB4BD'
                                    }
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )
                            }
                            onClick={() => handleLikeContent(viewContent)}
                          >
                            {viewContent?.totalLikesCount || 0}
                          </Button>
                        </Tooltip>

                        {!(
                          viewContent?.uploadType === 'Video' ||
                          viewContent?.uploadType === 'Audio'
                        ) && (
                          <Tooltip
                            placement="top"
                            title={
                              !viewContent?.download &&
                              'Download for the content is disabled.'
                            }
                          >
                            <Button
                              variant="outlined"
                              sx={{
                                px: 4,
                                borderColor: viewContent?.download
                                  ? colorPrimary
                                  : colorSecondary,
                                cursor: viewContent?.download
                                  ? 'pointer'
                                  : 'not-allowed',
                                fontSize: '12px',
                                fontWeight: 700,
                                marginRight: '10px',
                                borderRadius: '8px',
                                color: viewContent?.download
                                  ? 'black'
                                  : colorTextGrey,
                                borderWidth: '2px',
                                height: '34px',
                                width: '122px',
                                '&:hover': {
                                  borderColor: viewContent?.download
                                    ? colorPrimary
                                    : colorSecondary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '10px',
                                  borderWidth: '2px'
                                }
                              }}
                              startIcon={
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 15 15"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M12.75 12.75H2.25M11 6.91667L7.5 10.4167M7.5 10.4167L4 6.91667M7.5 10.4167V2.25"
                                    stroke={
                                      !viewContent?.download
                                        ? colorTextGrey
                                        : '#1F2933'
                                    }
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              }
                              onClick={() => handleDownload(viewContent)}
                            >
                              Download
                            </Button>
                          </Tooltip>
                        )}

                        <Tooltip
                          placement="top"
                          title={
                            !viewContent?.share &&
                            'Share for the content is disabled.'
                          }
                        >
                          <Button
                            variant="outlined"
                            sx={{
                              px: 4,
                              borderColor: viewContent?.share
                                ? colorPrimary
                                : colorSecondary,
                              cursor: viewContent?.share
                                ? 'pointer'
                                : 'not-allowed',
                              fontSize: '12px',
                              fontWeight: 700,
                              marginRight: '10px',
                              borderRadius: '8px',
                              color: viewContent?.share
                                ? 'black'
                                : colorTextGrey,
                              borderWidth: '2px',
                              height: '34px',
                              width: '97px',

                              '&:hover': {
                                borderColor: viewContent?.share
                                  ? colorPrimary
                                  : colorSecondary,
                                backgroundColor: 'transparent',
                                borderRadius: '10px',
                                borderWidth: '2px'
                              }
                            }}
                            startIcon={
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 13 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M11.6283 6.85461C11.7707 6.73255 11.8419 6.67152 11.868 6.5989C11.8909 6.53515 11.8909 6.46543 11.868 6.40169C11.8419 6.32906 11.7707 6.26803 11.6283 6.14597L6.68704 1.91061C6.4419 1.70049 6.31934 1.59544 6.21557 1.59286C6.12538 1.59063 6.03924 1.63025 5.98225 1.70018C5.91667 1.78064 5.91667 1.94207 5.91667 2.26493V4.77049C4.67144 4.98834 3.53176 5.61932 2.68483 6.56671C1.76148 7.59959 1.25072 8.93626 1.25 10.3217V10.6787C1.86212 9.9413 2.62638 9.34493 3.49044 8.93041C4.25224 8.56496 5.07574 8.34848 5.91667 8.29143V10.7357C5.91667 11.0585 5.91667 11.2199 5.98225 11.3004C6.03924 11.3703 6.12538 11.41 6.21557 11.4077C6.31934 11.4051 6.4419 11.3001 6.68704 11.09L11.6283 6.85461Z"
                                  stroke={
                                    !viewContent?.share
                                      ? colorTextGrey
                                      : '#1F2933'
                                  }
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            }
                            onClick={e => handleOnShareClick(e)}
                          >
                            Share
                          </Button>
                        </Tooltip>

                        <MoreActionsComponent
                          data={viewContent && viewContent}
                          isViewContentPage={true}
                          path={path}
                          onClick={handleOnReportClick}
                        />
                      </Box>
                    </div>
                  </div>
                  <div style={{ marginTop: '20px' }}>
                    <Divider />
                  </div>

                  <Typography
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '18px',
                      fontFamily: 'Lato',
                      color: colorLinkGrey,
                      marginTop: '10px'
                    }}
                  >
                    {isExpanded ? description : truncatedDescription}

                    {description?.length > 100 && (
                      <span
                        onClick={handleToggle}
                        style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          lineHeight: '18px',
                          fontFamily: 'Lato',
                          color: colorTextBlack,
                          marginTop: '10px',
                          cursor: 'pointer'
                        }}
                      >
                        {' '}
                        {isExpanded ? 'Show Less' : 'More'}
                      </span>
                    )}
                  </Typography>

                  {viewContent?.comments ? (
                    <div style={{ marginTop: '20px' }}>
                      <CommentSection
                        comments={commentsList}
                        handleCommentList={handleCommentList}
                        commentsCount={commentsCount}
                        contentId={viewContent?.contentId}
                        handleUpdateComment={handleUpdateComment}
                        commentPoints={viewContent?.comment_points}
                        allowPoints={viewContent?.allow_points}
                      />
                    </div>
                  ) : (
                    <div style={{ marginTop: '20px' }}>
                      <Typography
                        style={{
                          fontFamily: 'Lato',
                          fontSize: '14px',
                          fontWeight: 700,
                          color: colorTextGrey
                        }}
                      >
                        Comment for the content is disabled.
                      </Typography>
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item sx={{ flex: 4 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        lineHeight: '26px',
                        fontFamily: 'Lato',
                        color: colorTextBlack
                      }}
                    >
                      More classes
                    </Typography>

                    <Search>
                      <SearchIconWrapper>
                        <svg
                          width="21"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 17.5L14.375 13.875M16.3333 9.16667C16.3333 12.8486 13.3486 15.8333 9.66667 15.8333C5.98477 15.8333 3 12.8486 3 9.16667C3 5.48477 5.98477 2.5 9.66667 2.5C13.3486 2.5 16.3333 5.48477 16.3333 9.16667Z"
                            stroke="#ABB4BD"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </SearchIconWrapper>
                      <StyledInputBase
                        onChange={e => {
                          searchValue = e.target.value;
                          handleSearch(e);
                        }}
                        placeholder="Search here…"
                      />
                    </Search>
                  </div>

                  <FormControlLabel
                    sx={{
                      marginTop: '20px',
                      marginBottom: '20px',
                      color: colorLinkGrey,
                      '& .MuiFormControlLabel-label': {
                        color: colorLinkGrey,
                        fontSize: '14px',
                        lineHeight: '18px',
                        fontWeight: '600',
                        marginLeft: '1rem'
                      },
                      width: '50%'
                    }}
                    control={<IOSSwitch />}
                    checked={isAutoPlay}
                    onChange={e => handleAutoPlay(e)}
                    label={isAutoPlay ? 'Autoplay is on' : 'Autoplay is off'}
                    labelPlacement="end"
                  />

                  {isSectionWiseContentLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress />
                    </div>
                  ) : !isSectionWiseContentLoading && contentList.length > 0 ? (
                    <Grid container spacing={2} direction="column">
                      {contentList?.map((video, index) => (
                        <Grid
                          item
                          xs={12}
                          key={index}
                          onClick={() => setIsPlaying(!isPlaying)}
                        >
                          <ContentCards
                            video={video}
                            sectionId={sectionId}
                            tags={tags}
                            path={path || ''}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box
                      sx={{
                        marginBottom: 4,
                        backgroundColor: '#FFFFFF',
                        padding: '16px',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography
                        style={{
                          fontFamily: 'Lato',
                          fontSize: '14px',
                          fontWeight: 700,
                          color: colorTextGrey
                        }}
                      >
                        No content available
                      </Typography>
                    </Box>
                  )}
                </div>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      <ShareButtonDialog
        isOpen={isShareDialogOpen}
        onClose={handleCloseShareDialog}
        data={viewContent && viewContent}
      />

      {isQuizModalOpen && (
        <ViewQuizModal
          isOpen={isQuizModalOpen}
          onClose={handleCloseQuizModal}
          data={currentQuiz || 0}
          contentId={viewContent?.contentId || 0}
        />
      )}

      {!isUpdateLikeDislikeLoading &&
        isUpdateLikeDislikeSuccess &&
        updateLikeDislikeResponse && (
          <SnackBarBox
            type="success"
            message={handleError(updateLikeDislikeResponse?.payload?.message)}
          />
        )}

      {!isUpdateLikeDislikeLoading &&
        updateLikeDislikeErrorContainer &&
        isUpdateLikeDislikeFailed && (
          <SnackBarBox
            type="error"
            message={handleError(updateLikeDislikeErrorContainer?.errorMessage)}
          />
        )}

      {!isSaveToWatchLaterLoading &&
        isSaveToWatchLaterSuccess &&
        saveToWatchLaterResponse && (
          <SnackBarBox
            type="success"
            message={handleError(saveToWatchLaterResponse?.payload?.message)}
          />
        )}

      {!isSaveToWatchLaterLoading &&
        saveToWatchLaterErrorContainer &&
        isSaveToWatchLaterFailed && (
          <SnackBarBox
            type="error"
            message={handleError(saveToWatchLaterErrorContainer?.errorMessage)}
          />
        )}

      {!isShareDetailsLoading &&
        isShareDetailsSuccess &&
        shareDetailsResponse && (
          <SnackBarBox
            type="success"
            message={handleError(shareDetailsResponse?.payload?.message)}
          />
        )}

      {!isShareDetailsLoading &&
        shareDetailsErrorContainer &&
        isShareDetailsFailed && (
          <SnackBarBox
            type="error"
            message={handleError(shareDetailsErrorContainer?.errorMessage)}
          />
        )}

      {!isAddCommentsLoading && isAddCommentsSuccess && addCommentsResponse && (
        <SnackBarBox
          type="success"
          message={handleError(addCommentsResponse?.payload?.message)}
        />
      )}

      {!isAddCommentsLoading &&
        addCommentsErrorContainer &&
        isAddCommentsFailed && (
          <SnackBarBox
            type="error"
            message={handleError(addCommentsErrorContainer?.errorMessage)}
          />
        )}
    </>
  );
};

export default ViewContent;
